/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — notes-questionnaire.ts
   Orchestrator + drag-and-drop UI for generated notes.
   Section builders live in notes-builders.ts.
   Auto-populate logic lives in notes-autopopulate.ts.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { meta, esc, el } from './utils';
import { saveNotesToProject } from './data';
import { autoPopulateFromTB } from './notes-autopopulate';
import { DEFAULT_NOTE_ORDER, NOTE_BUILDERS, type NoteSectionResult } from './notes-builders';

// Re-export so the existing barrel (notes.ts) keeps working
export { autoPopulateFromTB } from './notes-autopopulate';
export { DEFAULT_NOTE_ORDER } from './notes-builders';

// ─── Drag-and-Drop Helpers ─────────────────────────────────────────────────

let _draggedNoteId: string | null = null;

function initNoteDragAndDrop(): void {
  const container = el('notes-output-sections');
  if (!container) return;

  const sections = container.querySelectorAll<HTMLElement>('.note-section-draggable');
  sections.forEach((section: HTMLElement) => {
    section.addEventListener('dragstart', (e: DragEvent) => {
      _draggedNoteId = section.dataset.noteId || null;
      section.classList.add('note-dragging');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', _draggedNoteId || '');
      }
    });

    section.addEventListener('dragend', () => {
      section.classList.remove('note-dragging');
      _draggedNoteId = null;
      container.querySelectorAll('.note-drop-target').forEach((dropEl: Element) => {
        dropEl.classList.remove('note-drop-target');
      });
    });

    section.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      container.querySelectorAll('.note-drop-target').forEach((dropEl: Element) => {
        dropEl.classList.remove('note-drop-target');
      });
      if (_draggedNoteId && section.dataset.noteId !== _draggedNoteId) {
        section.classList.add('note-drop-target');
      }
    });

    section.addEventListener('dragleave', () => {
      section.classList.remove('note-drop-target');
    });

    section.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      section.classList.remove('note-drop-target');
      if (!_draggedNoteId || _draggedNoteId === section.dataset.noteId) return;

      const draggedEl = container.querySelector('[data-note-id="' + _draggedNoteId + '"]') as HTMLElement | null;
      if (!draggedEl) return;

      // Determine drop position based on mouse Y relative to target center
      const rect = section.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const mouseY = e.clientY;

      if (mouseY < midY) {
        container.insertBefore(draggedEl, section);
      } else {
        container.insertBefore(draggedEl, section.nextSibling);
      }

      // Update state.noteOrder from current DOM order
      const updatedOrder: string[] = [];
      container.querySelectorAll<HTMLElement>('.note-section-draggable').forEach((s: HTMLElement) => {
        if (s.dataset.noteId) updatedOrder.push(s.dataset.noteId);
      });
      state.noteOrder = updatedOrder;

      // Re-number notes after reorder
      renumberNotes();

      saveNotesToProject();
    });
  });
}

function renumberNotes(): void {
  const container = el('notes-output-sections');
  if (!container) return;
  let num: number = 0;
  container.querySelectorAll<HTMLElement>('.note-section-draggable').forEach((section: HTMLElement) => {
    num++;
    const h3 = section.querySelector('.note-section-title');
    if (h3) {
      const titleText = h3.getAttribute('data-base-title') || '';
      h3.textContent = 'Note ' + num + ' — ' + titleText;
    }
  });
}

function resetNoteOrder(): void {
  state.noteOrder = [];
  generateNotes();
}

// ─── Drag-and-Drop Styles ──────────────────────────────────────────────────

function injectNoteReorderStyles(): void {
  if (document.getElementById('note-reorder-styles')) return;
  const style: HTMLStyleElement = document.createElement('style');
  style.id = 'note-reorder-styles';
  style.textContent = `
    .note-section-draggable {
      position: relative;
      transition: box-shadow 0.15s, border-color 0.15s;
      border: 2px solid transparent;
      border-radius: 6px;
      margin-bottom: 4px;
      padding: 2px 0 2px 0;
    }
    .note-section-draggable:hover {
      border-color: #e0e0e0;
    }
    .note-section-draggable.note-dragging {
      opacity: 0.4;
      border-color: #2196f3;
    }
    .note-section-draggable.note-drop-target {
      border-color: #2196f3;
      box-shadow: 0 0 0 2px rgba(33,150,243,0.2);
      background: rgba(33,150,243,0.04);
    }
    .note-drag-handle {
      display: inline-block;
      cursor: grab;
      color: #999;
      font-size: 1.1em;
      margin-right: 6px;
      vertical-align: middle;
      user-select: none;
    }
    .note-drag-handle:hover {
      color: #555;
    }
    .note-section-draggable .note-section-header {
      display: flex;
      align-items: center;
    }
    .note-section-title {
      flex: 1;
    }
    .note-reorder-toolbar {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 8px;
      gap: 8px;
    }
    .note-reorder-toolbar button {
      font-size: 0.82rem;
      padding: 4px 12px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background: #f5f5f5;
      cursor: pointer;
      color: #555;
    }
    .note-reorder-toolbar button:hover {
      background: #e8e8e8;
      border-color: #999;
    }
    @media print {
      .note-drag-handle,
      .note-reorder-toolbar {
        display: none !important;
      }
      .note-section-draggable {
        border: none !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
      }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Generates notes to the financial statements from questionnaire inputs and renders them to the DOM.
 * Notes are rendered in the order specified by state.noteOrder (or DEFAULT_NOTE_ORDER if empty).
 * Each section is wrapped in a draggable container for reordering.
 */
export function generateNotes(): void {
  // Ensure questionnaire is pre-filled from TB data before generating
  autoPopulateFromTB();

  const m = meta();
  const company: string = m.company;
  const period: string = m.period;
  const priorPeriod: string = m.priorPeriod;

  // Determine section order
  const order: string[] = (state.noteOrder && state.noteOrder.length > 0)
    ? state.noteOrder
    : DEFAULT_NOTE_ORDER;

  // Build all sections that have content, in requested order
  const builtSections: NoteSectionResult[] = [];
  const builtIds = new Set<string>();
  for (const sectionId of order) {
    const builder = NOTE_BUILDERS[sectionId];
    if (!builder) continue;
    const result = builder(company, period, priorPeriod);
    if (result) {
      builtSections.push(result);
      builtIds.add(sectionId);
    }
  }
  // Also build any sections not in the order list (new sections or corrupted order)
  for (const sectionId of DEFAULT_NOTE_ORDER) {
    if (builtIds.has(sectionId)) continue;
    const builder = NOTE_BUILDERS[sectionId];
    if (!builder) continue;
    const result = builder(company, period, priorPeriod);
    if (result) {
      builtSections.push(result);
    }
  }

  // Build the HTML
  let html: string = '<div class="stmt-header">' +
    '<div class="company">' + esc(company) + '</div>' +
    '<div class="title">Notes to the Financial Statements</div>' +
    '<div class="period">' + esc(period) + '</div>' +
  '</div>';

  // Reorder toolbar
  html += '<div class="note-reorder-toolbar">' +
    '<button type="button" id="btn-reset-note-order" title="Restore default note ordering">Reset Order</button>' +
  '</div>';

  html += '<div class="notes-output" id="notes-output-sections">';

  let noteNum: number = 0;
  for (const section of builtSections) {
    noteNum++;
    html += '<div class="note-section-draggable" draggable="true" data-note-id="' + section.id + '">';
    html += '<h3 class="note-section-header">' +
      '<span class="note-drag-handle" title="Drag to reorder">☰</span>' +
      '<span class="note-section-title" data-base-title="' + esc(section.title) + '">' +
      'Note ' + noteNum + ' — ' + esc(section.title) +
      '</span></h3>';
    html += section.html;
    html += '</div>';
  }

  html += '</div>';

  const notesStmt = el('notes-statement');
  if (notesStmt) notesStmt.innerHTML = html;
  const notesQ = el('notes-questionnaire');
  if (notesQ) notesQ.style.display = 'none';
  const notesOut = el('notes-output-wrapper');
  if (notesOut) notesOut.style.display = 'block';

  // Inject styles and initialize drag-and-drop
  injectNoteReorderStyles();
  initNoteDragAndDrop();

  // Wire up reset button
  const resetBtn = document.getElementById('btn-reset-note-order');
  if (resetBtn) {
    resetBtn.addEventListener('click', resetNoteOrder);
  }

  saveNotesToProject();
}
